// effective-config-v2-loader.ts
//
// V2 spec §7 "Config ↔ CLI": the V2 half of effective-config-loader.ts —
// split out because the main loader sits at the 200-line budget.
//   - The V2 file is the main file's SIBLING: `autopilot-config.json` ⇒
//     `autopilot-config.v2.json`, same directory (derived, never a second
//     hard-coded home path — tests inject a reader and never touch `~`).
//   - Missing file (ENOENT) ⇒ schema defaults. Unreadable / bad JSON / schema
//     violation ⇒ `config_invalid`, same shape and no-echo rule as v1.
//   - A V2 key found in the MAIN file is refused with a pointer to the V2
//     file — never read from there, so the main file stays readable by CLI
//     3.34.x (whose strict schema rejects the whole file on an unknown key).
//   - Snapshot `effective-config.v2.json` in the control root (E12), written
//     on every load that has a control root, defaults included.
import { basename, dirname, join } from "node:path";
import {
  type ConfigFileLoadErr,
  configFileError,
} from "./config-file-error-without-content-echo.js";
import { writeFileDurable } from "./durable-file-writer.js";
import {
  AUTOPILOT_CONFIG_V2_KEYS,
  type AutopilotConfigV2,
  AutopilotConfigV2Schema,
} from "./schemas/config-v2-schema.js";

export const EFFECTIVE_CONFIG_V2_FILE_NAME = "effective-config.v2.json";

/** `<dir>/autopilot-config.json` ⇒ `<dir>/autopilot-config.v2.json` (a name without `.json` just gains `.v2.json`). */
export function configV2PathFor(globalConfigPath: string): string {
  const name = basename(globalConfigPath);
  const stem = name.endsWith(".json") ? name.slice(0, -".json".length) : name;
  return join(dirname(globalConfigPath), `${stem}.v2.json`);
}

/**
 * The first V2 key sitting at the top level of the MAIN file's parsed JSON,
 * as a `config_invalid` error pointing at the V2 file — `undefined` when none.
 * Checked BEFORE the v1 schema so the person gets the pointer, not v1's
 * generic "unrecognized key". The value is never echoed.
 */
export function findV2KeyMisplacedInMainConfig(
  rawMainConfig: unknown,
  globalConfigPath: string,
): ConfigFileLoadErr | undefined {
  if (rawMainConfig === null || typeof rawMainConfig !== "object") return undefined;
  const key = AUTOPILOT_CONFIG_V2_KEYS.find((k) => Object.hasOwn(rawMainConfig, k));
  if (key === undefined) return undefined;
  return {
    ok: false,
    fieldPath: key,
    message:
      `Key "${key}" là config V2 — chuyển sang "${configV2PathFor(globalConfigPath)}". ` +
      `File chính "${globalConfigPath}" phải giữ đúng shape v1 để CLI 3.34.x ở workspace khác vẫn đọc được.`,
  };
}

export type ConfigV2LoadResult = { ok: true; configV2: AutopilotConfigV2 } | ConfigFileLoadErr;

async function defaultReadText(path: string): Promise<string> {
  const { readFile } = await import("node:fs/promises");
  return readFile(path, "utf8");
}

/** Reads + validates the V2 file once. Not frozen here — the caller freezes its whole result. */
export async function loadConfigV2(input: {
  globalConfigPath: string;
  /** Raw text of the V2 file; rejects with `code === "ENOENT"` ⇒ defaults. */
  readGlobalConfigV2?: () => Promise<string>;
}): Promise<ConfigV2LoadResult> {
  const path = configV2PathFor(input.globalConfigPath);
  const read = input.readGlobalConfigV2 ?? (() => defaultReadText(path));
  let text: string | undefined;
  try {
    text = await read();
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") {
      return configFileError(path, "không đọc được", err);
    }
  }
  let raw: unknown = {};
  if (text !== undefined) {
    try {
      raw = JSON.parse(text);
    } catch (err) {
      return configFileError(path, "JSON sai cú pháp", err);
    }
  }
  const parsed = AutopilotConfigV2Schema.safeParse(raw);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const fieldPath = issue ? issue.path.join(".") || "(file)" : "(unknown)";
    return {
      ok: false,
      fieldPath,
      message: `Global config V2 "${path}" không hợp lệ ở field "${fieldPath}": ${
        issue ? issue.message : parsed.error.message
      }`,
    };
  }
  return { ok: true, configV2: parsed.data };
}

/** E12: the run's frozen V2 values, next to `effective-config.json`. */
export async function writeConfigV2Snapshot(
  controlRoot: string,
  configV2: AutopilotConfigV2,
): Promise<void> {
  await writeFileDurable(
    join(controlRoot, EFFECTIVE_CONFIG_V2_FILE_NAME),
    Buffer.from(`${JSON.stringify(configV2, null, 2)}\n`, "utf8"),
  );
}
